import React, { useEffect, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { AdminI18nProvider } from "@/lib/adminI18n";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import HowItWorks from "@/components/HowItWorks";
import ShopByCountry from "@/components/ShopByCountry";
import StorePreview from "@/components/StorePreview";
import WhyChooseUs from "@/components/WhyChooseUs";
import Categories from "@/components/Categories";
import RatesSection from "@/components/RatesSection";
import WorkVideos from "@/components/WorkVideos";
import TeamSection from "@/components/TeamSection";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import FinalCTA from "@/components/FinalCTA";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import CountryPage from "@/pages/CountryPage";
import ShippingOnlyPage from "@/pages/ShippingOnlyPage";
import ShippingRatesPage from "@/pages/ShippingRatesPage";
import ChinaSuppliersPage from "@/pages/ChinaSuppliersPage";
import EmbedPage from "@/pages/EmbedPage";
import StockPage from "@/pages/StockPage";
import SheinPage from "@/pages/SheinPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminShippingPage from "@/pages/AdminShippingPage";
import AdminShippingOnlyPage from "@/pages/AdminShippingOnlyPage";
import AdminWarehousesPage from "@/pages/AdminWarehousesPage";
import AdminCustomersPage from "@/pages/AdminCustomersPage";
import AdminContentPage from "@/pages/AdminContentPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminApprovalsPage from "@/pages/AdminApprovalsPage";
import AdminNotificationsPage from "@/pages/AdminNotificationsPage";
import AdminSocialPostsPage from "@/pages/AdminSocialPostsPage";
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/CustomerAuthPages";
import { AccountPage, OrdersPage } from "@/pages/CustomerAccountPages";
import {
  FinancialDashboardPage,
  FinancialHubPage,
  FinancialInvoicesPage,
  FinancialLinkPage,
  FinancialOrderDetailPage,
  FinancialOrdersPage,
  FinancialPaymentsPage,
  FinancialStatementPage,
} from "@/pages/FinancialPages";
import { AccountDeletionPage, TermsPage, PrivacyPage, ProhibitedItemsPage } from "@/pages/LegalPages";
import { CartProvider } from "@/context/CartContext";
import { StockProvider } from "@/context/StockContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { CustomerAuthProvider, useCustomerAuth } from "@/context/CustomerAuthContext";
import { OdooProvider } from "@/context/OdooContext";
import { CatalogProvider } from "@/context/CatalogContext";
import CartDrawer from "@/components/store/CartDrawer";
import StickyCartButton from "@/components/store/StickyCartButton";
import { trackEvent } from "@/lib/analytics";

const Landing = () => (
  <main className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] transition-colors dark:bg-[#080808] dark:text-[#F6F7FB]">
    <Navbar />
    <Hero />
    <HowItWorks />
    <ShopByCountry />
    <StorePreview />
    <WhyChooseUs />
    <Categories />
    <RatesSection />
    <WorkVideos />
    <Testimonials />
    <FAQ />
    <TeamSection />
    <FinalCTA />
    <Footer />
    <FloatingWhatsApp />
    <CartDrawer />
    <StickyCartButton />
  </main>
);

function RouteAnalyticsTracker() {
  const location = useLocation();
  const { lang } = useLanguage();
  const { theme } = useTheme();
  const { customer } = useCustomerAuth();

  const routeName = useMemo(() => {
    const path = location.pathname || "/";
    if (path === "/") return "home";
    if (path === "/stock") return "store";
    if (path === "/stock/shein") return "shein";
    if (path.startsWith("/stock/")) return "product_detail";
    if (path.startsWith("/country/")) return "country";
    if (path === "/shipping-rates") return "shipping_rates";
    if (path === "/shipping-only") return "shipping_only";
    if (path.startsWith("/embed/")) return "embed";
    if (path === "/login") return "login";
    if (path === "/signup") return "signup";
    if (path === "/forgot-password") return "forgot_password";
    if (path === "/reset-password") return "reset_password";
    if (path === "/account") return "account";
    if (path === "/orders") return "orders";
    if (path.startsWith("/financial")) return "financial";
    if (path === "/terms") return "terms";
    if (path === "/privacy") return "privacy";
    if (path === "/prohibited-items") return "prohibited_items";
    if (path === "/china-suppliers-database") return "china_suppliers";
    return "public";
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname || location.pathname.startsWith("/admin")) return;
    trackEvent("page_view", {
      pagePath: `${location.pathname}${location.search || ""}`,
      routeName,
      language: lang,
      theme,
      customerId: customer?.id,
      metadata: {
        page_title: typeof document !== "undefined" ? document.title : "",
      },
    });
  }, [location.pathname, location.search, routeName]);

  return null;
}

function AppShell() {
  const { theme } = useTheme();

  return (
    <div className="App bg-[#FAFAFA] text-[#111] transition-colors dark:bg-[#080808] dark:text-[#F6F7FB]">
      <Toaster
        position="top-center"
        richColors
        theme={theme}
        toastOptions={{
          style: {
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
          },
        }}
      />
      <BrowserRouter>
        <RouteAnalyticsTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/country/:code" element={<CountryPage />} />
          <Route path="/shipping-only" element={<ShippingOnlyPage />} />
          <Route path="/shipping-rates" element={<ShippingRatesPage />} />
          <Route path="/embed/:embedKey" element={<EmbedPage />} />
          <Route path="/china-suppliers-database" element={<ChinaSuppliersPage />} />
          <Route path="/products" element={<Navigate to="/stock" replace />} />
          <Route path="/stock" element={<StockPage />} />
          <Route path="/stock/shein" element={<SheinPage />} />
          <Route path="/stock/:slug" element={<ProductDetailPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/shipping" element={<AdminShippingPage />} />
          <Route path="/admin/shipping-only" element={<AdminShippingOnlyPage />} />
          <Route path="/admin/warehouses" element={<AdminWarehousesPage />} />
          <Route path="/admin/customers" element={<AdminCustomersPage />} />
          <Route path="/admin/content" element={<AdminContentPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
          <Route path="/admin/approvals" element={<AdminApprovalsPage />} />
          <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
          <Route path="/admin/social-posts" element={<AdminSocialPostsPage />} />
          {/* Customer auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Customer account (protected) */}
          <Route path="/account" element={<AccountPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/financial" element={<FinancialHubPage />} />
          <Route path="/financial/orders" element={<FinancialOrdersPage />} />
          <Route path="/financial/orders/:id" element={<FinancialOrderDetailPage />} />
          <Route path="/financial/invoices" element={<FinancialInvoicesPage />} />
          <Route path="/financial/payments" element={<FinancialPaymentsPage />} />
          <Route path="/financial/statement" element={<FinancialStatementPage />} />
          <Route path="/financial/dashboard" element={<FinancialDashboardPage />} />
          <Route path="/financial/link" element={<FinancialLinkPage />} />
          {/* Legal */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/prohibited-items" element={<ProhibitedItemsPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <StockProvider>
          <CartProvider>
            <AdminAuthProvider>
              <CustomerAuthProvider>
                <OdooProvider>
                  <CatalogProvider>
                    <AdminI18nProvider>
                      <AppShell />
                    </AdminI18nProvider>
                  </CatalogProvider>
                </OdooProvider>
              </CustomerAuthProvider>
            </AdminAuthProvider>
          </CartProvider>
        </StockProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
